import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Newspaper, Search, X } from "lucide-react";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/dibs_landing_page/Navbar";
import Footer from "@/components/dibs_landing_page/Footer";

import type { PortableTextBlock } from "@portabletext/types";
import {
  sanityClient,
  postListQuery,
  urlForImage,
  CATEGORY_LABELS,
  type PostCategory,
  type PostListItem,
} from "@/lib/sanity";

const WORDS_PER_MINUTE = 200;

const portableTextToPlain = (blocks?: PortableTextBlock[]): string => {
  if (!blocks?.length) return "";
  return blocks
    .filter((b) => b._type === "block")
    .map((block) => {
      const children = (block as { children?: { text?: string }[] }).children;
      return children?.map((c) => c.text ?? "").join("") ?? "";
    })
    .join(" ");
};

const readingTimeMinutes = (post: Pick<PostListItem, "excerpt" | "body">): number => {
  const text = [post.excerpt ?? "", portableTextToPlain(post.body)].join(" ").trim();
  const words = text.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / WORDS_PER_MINUTE));
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

const postPreview = (post: Pick<PostListItem, "excerpt" | "body">): string | undefined => {
  if (post.excerpt?.trim()) return post.excerpt.trim();
  const plain = portableTextToPlain(post.body).trim();
  if (!plain) return undefined;
  return plain.length > 200 ? `${plain.slice(0, 200).trimEnd()}…` : plain;
};

const ReadingTime = ({ minutes }: { minutes: number }) => (
  <span className="text-xs text-muted-foreground tabular-nums">{minutes} min read</span>
);

const categoryClasses = (c?: PostCategory) => {
  switch (c) {
    case "product":
      return "bg-primary/10 text-primary";
    case "company":
      return "bg-navy/10 text-navy";
    case "research":
      return "bg-foreground/[0.08] text-foreground";
    default:
      return "bg-muted text-muted-foreground";
  }
};

const CategoryBadge = ({ category }: { category?: PostCategory }) => {
  if (!category) return null;
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${categoryClasses(category)}`}
    >
      {CATEGORY_LABELS[category]}
    </span>
  );
};

type Filter = "all" | PostCategory;

const FILTERS: { value: Filter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "product", label: "Product" },
  { value: "company", label: "Company" },
  { value: "research", label: "Research" },
];

const BlogList = () => {
  const [posts, setPosts] = useState<PostListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [query, setQuery] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    sanityClient
      .fetch<PostListItem[]>(postListQuery)
      .then(setPosts)
      .catch((e) => setError(e.message ?? "Failed to load posts"));
  }, []);

  const filtered = useMemo(() => {
    if (!posts) return null;
    const q = query.trim().toLowerCase();
    return posts.filter((p) => {
      if (filter !== "all" && p.category !== filter) return false;
      if (!q) return true;
      return (
        p.title.toLowerCase().includes(q) ||
        (p.excerpt?.toLowerCase().includes(q) ?? false) ||
        (p.author?.toLowerCase().includes(q) ?? false)
      );
    });
  }, [posts, filter, query]);

  const [featured, ...rest] = filtered ?? [];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Blog — Dibs</title>
        <meta name="description" content="News, product updates, and stories from Dibs." />
        <link rel="canonical" href="https://www.usedibs.com/blog" />
        <meta property="og:title" content="Blog — Dibs" />
        <meta property="og:description" content="News, product updates, and stories from Dibs." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.usedibs.com/blog" />
        <meta property="og:image" content="https://www.usedibs.com/dibs_assets/red_logo_with_transparent_bg.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Blog — Dibs" />
        <meta name="twitter:description" content="News, product updates, and stories from Dibs." />
      </Helmet>

      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/[0.06] via-background to-background pointer-events-none" />
        <div className="absolute -top-24 right-0 w-72 h-72 rounded-full bg-primary/5 blur-3xl pointer-events-none" />
        <div className="max-w-5xl mx-auto px-4 pt-24 sm:pt-28 pb-10 sm:pb-12 relative">
          <div className="animate-fade-up max-w-2xl mx-auto text-center">
            <p className="text-xs font-extrabold uppercase tracking-[0.12em] text-primary mb-[18px]">
              Dibs Blog
            </p>
            <h1 className="text-[clamp(2rem,3.4vw,3.1rem)] leading-[1.08] font-heading font-[750] tracking-[-0.05em] text-foreground text-balance mb-5">
              Only the realest content
            </h1>
            <p className="text-muted-foreground text-[17px] leading-[1.7] mb-8">
              Product news, company updates, and ideas on supercharging your business with Dibs.
            </p>
          </div>

          <div className="animate-fade-up flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search posts…"
                className="w-full h-10 pl-11 pr-10 rounded-lg bg-card/80 backdrop-blur-sm border border-border shadow-sm text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary/40 transition-all"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label="Clear search"
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {posts && posts.length > 0 && (
              <div
                className="flex items-center gap-6 sm:gap-8 shrink-0"
                role="tablist"
                aria-label="Filter by category"
              >
                {FILTERS.map((f) => {
                  const active = filter === f.value;
                  return (
                    <button
                      key={f.value}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setFilter(f.value)}
                      className={`pb-1 text-sm font-semibold transition-colors border-b-2 whitespace-nowrap ${
                        active
                          ? "text-foreground border-primary"
                          : "text-muted-foreground border-transparent hover:text-foreground"
                      }`}
                    >
                      {f.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 pt-2 sm:pt-4">
        {error && (
          <div className="module-card border-destructive/30 bg-destructive/5">
            <p className="text-destructive text-sm">{error}</p>
          </div>
        )}

        {!error && posts === null && (
          <div className="grid gap-6 sm:grid-cols-2">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="module-card animate-pulse">
                <div className="aspect-[16/10] w-full bg-muted rounded-xl mb-5" />
                <div className="h-3 w-24 bg-muted rounded mb-3" />
                <div className="h-5 w-3/4 bg-muted rounded mb-3" />
                <div className="h-3 w-full bg-muted rounded mb-2" />
                <div className="h-3 w-5/6 bg-muted rounded" />
              </div>
            ))}
          </div>
        )}

        {posts && posts.length === 0 && (
          <div className="module-card flex flex-col items-center text-center py-16">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-5">
              <Newspaper className="w-6 h-6 text-primary" />
            </div>
            <h2 className="text-xl font-heading font-semibold mb-2">No posts yet</h2>
            <p className="text-muted-foreground text-sm max-w-sm">
              We're just getting started. Check back soon for product updates and stories from the team.
            </p>
          </div>
        )}

        {posts && posts.length > 0 && filtered && filtered.length === 0 && (
          <div className="module-card flex flex-col items-center text-center py-12 mb-16 sm:mb-20">
            <p className="text-muted-foreground text-sm">
              {query ? (
                <>No posts match &ldquo;<span className="font-semibold text-foreground">{query}</span>&rdquo;.</>
              ) : (
                <>No posts in <span className="font-semibold text-foreground">{CATEGORY_LABELS[filter as PostCategory]}</span> yet.</>
              )}
            </p>
          </div>
        )}

        {/* Featured post (first) */}
        {featured && (
          <Link
            to={`/blog/${featured.slug}`}
            className="group block rounded-xl border border-border/60 bg-card shadow-card hover:-translate-y-0.5 hover:shadow-card-hover transition-all duration-300 mb-0 animate-fade-up overflow-hidden"
          >
            <div className="grid md:grid-cols-[1.05fr_1fr] gap-0">
              {featured.mainImage ? (
                <div className="aspect-[16/10] md:aspect-auto md:min-h-[320px] overflow-hidden bg-muted">
                  <img
                    src={urlForImage(featured.mainImage).width(1200).height(800).fit("crop").url()}
                    alt={featured.mainImage.alt ?? ""}
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out"
                    loading="eager"
                  />
                </div>
              ) : (
                <div className="hidden md:block min-h-[320px] bg-gradient-to-br from-primary/15 via-primary/5 to-transparent" />
              )}
              <div className="p-6 sm:p-8 lg:p-10 flex flex-col justify-center border-t md:border-t-0 md:border-l border-border/50">
                <div className="flex items-center flex-wrap gap-2 mb-4">
                  <CategoryBadge category={featured.category} />
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {formatDate(featured.publishedAt)}
                  </span>
                  <span className="text-xs text-muted-foreground">·</span>
                  <ReadingTime minutes={readingTimeMinutes(featured)} />
                </div>
                <h2 className="text-2xl sm:text-[1.75rem] lg:text-3xl font-heading font-[750] tracking-[-0.05em] leading-[1.08] text-balance mb-3 group-hover:text-primary transition-colors">
                  {featured.title}
                </h2>
                {postPreview(featured) && (
                  <p className="text-muted-foreground text-[15px] leading-[1.7] mb-5 line-clamp-3">
                    {postPreview(featured)}
                  </p>
                )}
                <div className="mt-auto pt-2">
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary group-hover:gap-2.5 transition-all">
                    Read article
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </div>
          </Link>
        )}

        {/* Rest of posts as grid */}
        {rest.length > 0 && (
          <>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground text-center mb-7 mt-12">
              More posts
            </p>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {rest.map((p, idx) => (
                <Link
                  key={p._id}
                  to={`/blog/${p.slug}`}
                  className={`group flex flex-col rounded-xl border border-border/60 bg-card shadow-card hover:-translate-y-0.5 hover:shadow-card-hover transition-all duration-300 overflow-hidden animate-fade-up ${
                    idx === 0 ? "animate-fade-up-delay-1" : idx === 1 ? "animate-fade-up-delay-2" : "animate-fade-up-delay-3"
                  }`}
                >
                  {p.mainImage ? (
                    <div className="aspect-[16/10] overflow-hidden bg-muted">
                      <img
                        src={urlForImage(p.mainImage).width(720).height(450).fit("crop").url()}
                        alt={p.mainImage.alt ?? ""}
                        className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="aspect-[16/10] bg-gradient-to-br from-primary/12 to-primary/5" />
                  )}
                  <div className="p-5 sm:p-6 flex-1 flex flex-col">
                    <div className="flex items-center flex-wrap gap-2 mb-3">
                      <CategoryBadge category={p.category} />
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {formatDate(p.publishedAt)}
                      </span>
                      <span className="text-xs text-muted-foreground">·</span>
                      <ReadingTime minutes={readingTimeMinutes(p)} />
                    </div>
                    <h3 className="text-[19px] font-heading font-[750] tracking-[-0.02em] mb-2 group-hover:text-primary transition-colors line-clamp-2 leading-snug">
                      {p.title}
                    </h3>
                    {postPreview(p) && (
                      <p className="text-muted-foreground text-sm leading-[1.6] line-clamp-3 mb-4 flex-1">
                        {postPreview(p)}
                      </p>
                    )}
                    <div className="mt-auto pt-3 flex items-center justify-end gap-2 border-t border-border/50">
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary shrink-0 group-hover:gap-1.5 transition-all">
                        Read
                        <ArrowRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </section>

      {/* The footer card overlaps the content above it by 88px, so add that on top of the visible gap. */}
      <div className="pb-[152px] sm:pb-[184px]" />

      <Footer />
    </div>
  );
};

export default BlogList;
